import { MouseEventHandler, ReactNode, useCallback, useEffect } from 'react'
import classNames from 'classnames'
import './Menu.css'

export interface MenuButtonProps {
  buttonBorder?: boolean
  children: ReactNode
  menuIcon: ReactNode
  menuName: string
  onToggleMenu: (menuName: string) => void
  openMenu: string
}

export type MenuButtonControlProps = Omit<
  MenuButtonProps,
  'children' | 'menuIcon' | 'menuName'
>

export const MenuButton = ({
  buttonBorder = true,
  children,
  menuIcon,
  menuName,
  onToggleMenu,
  openMenu,
}: MenuButtonProps) => {
  const isOpen = openMenu === menuName

  const handleToggleMenu = useCallback(() => {
    onToggleMenu(menuName)
  }, [menuName, onToggleMenu])

  const handleClick: MouseEventHandler = useCallback(
    (evt) => {
      evt.stopPropagation()
      evt.preventDefault()
      handleToggleMenu()
    },
    [handleToggleMenu],
  )

  useEffect(() => {
    // When the menu is open, close it when clicking anywhere in the app.
    if (isOpen) document.addEventListener('click', handleToggleMenu)
    return () => document.removeEventListener('click', handleToggleMenu)
  }, [handleToggleMenu, isOpen])

  return (
    <div
      className={classNames('menu-container', 'icon-container', {
        'icon-border': buttonBorder,
      })}
    >
      <button className="icon-button" onClick={handleClick}>
        {menuIcon}
        <div className="icon-caption">{menuName}</div>
      </button>

      {isOpen && (
        <>
          <ul className="menu">{children}</ul>
        </>
      )}
    </div>
  )
}
